// Levels in Lazy cats. The id is fixed (progress is saved under it) – never change it once a level is published.
import { defineLevels } from '../../core/registry';
import { on, top } from '../../core/level-helpers';
import type { LevelData, ObjSpec, PlacementData, StaticData } from '../../core/types';
import { BOOK, type BookType } from './shared';

const P = 15;
/** Shelf (22 high); a = angle in degrees */
const sh = (x: number, y: number, w: number, a?: number, skin?: StaticData['skin']): StaticData => ({ x, y, w, h: 22, a: a || 0, skin: skin || 'cats_shelf', friction: 0.8 });
/** Book standing on shelf s at x */
const bookAt = (s: StaticData, x: number, t?: BookType): PlacementData => ({ t: t || 'cats_book', x, y: Math.round(top(s) - BOOK[t || 'cats_book'].h / 2 - 1) });
/** Sleeping cat lying on shelf s at x; dir 1 = head to the right */
const catAt = (s: StaticData, x: number, dir: number, extra?: Partial<ObjSpec>): ObjSpec => Object.assign({ t: 'cats_cat' as const, x, y: top(s) - 23, dir }, extra || {});
const L: LevelData[] = [];

{ // 1 – The book topples – intro: books fall like dominoes and pass the push on
  const s0 = sh(150, 250, 240, 14), s1 = sh(470, 440, 500);
  L.push({
    id: 'book-topples', par: 1, tools: { cats_book: 1 }, learn: ['chain_reaction', 'energy'], intro: 'cats_book',
    statics: [s0, s1],
    fixed: [bookAt(s1, 420), bookAt(s1, 490)],
    balls: [on(s0, -80, 13, { helper: true }), on(s1, 215, P)],
    goal: { type: 'bowl', x: 830, y: 700, w: 180 },
    objs: [{ t: 'duck', x: 560, y: 250 }],
    solution: [{ t: 'cats_book', x: 600, y: 360 }],
  });
}

{ // 2 – Wake the cat – intro: the last book must reach the sleeping cat, which then swats the yarn ball into the basket
  const s0 = sh(150, 250, 240, 14), s1 = sh(500, 440, 640);
  L.push({
    id: 'wake-the-cat', par: 1, tools: { cats_book: 1 }, learn: ['chain_reaction', 'energy'], intro: 'cats_cat',
    statics: [s0, s1],
    fixed: [bookAt(s1, 400), bookAt(s1, 470)],
    balls: [on(s0, -80, 13, { helper: true }), on(s1, 268, P)],
    goal: { type: 'bowl', x: 930, y: 720, w: 180 },
    objs: [catAt(s1, 680, 1, { power: 5 }), { t: 'duck', x: 880, y: 470 }],
    solution: [{ t: 'cats_book', x: 560, y: 360 }],
  });
}
{ // 3 – The toy mouse – intro: the mouse on a string swings and pushes the yarn ball
  const s1 = sh(450, 520, 460);
  L.push({
    id: 'toy-mouse', par: 1, tools: { cats_pendulum: 1 }, learn: ['energy', 'chain_reaction'], intro: 'cats_pendulum',
    statics: [s1, { x: 1010, y: 640, w: 30, h: 200, skin: 'cats_post' }],
    balls: [on(s1, -80, P)],
    goal: { type: 'bowl', x: 870, y: 730, w: 190 },
    objs: [{ t: 'duck', x: 560, y: 430 }],
    solution: [{ t: 'cats_pendulum', x: 370, y: 350, a: 0 }],
  });
}
{ // 4 – Big or small – choose the book: only the tall book reaches all the way to the cat (the booklet is a decoy)
  const s0 = sh(150, 250, 240, 14), s1 = sh(500, 440, 640);
  L.push({
    id: 'big-or-small', par: 1, tools: { cats_booklet: 1, cats_book: 1 }, learn: ['chain_reaction', 'energy'],
    statics: [s0, s1, { x: 1060, y: 640, w: 30, h: 220, skin: 'cats_post' }],
    fixed: [bookAt(s1, 400), bookAt(s1, 470)],
    balls: [on(s0, -80, 13, { helper: true }), on(s1, 300, P)],
    goal: { type: 'bowl', x: 940, y: 720, w: 180 },
    objs: [catAt(s1, 712, 1, { col: 'gray', power: 5 }), { t: 'duck', x: 560, y: 300 }],
    solution: [{ t: 'cats_book', x: 565, y: 360 }],
    fails: [[{ t: 'cats_booklet', x: 565, y: 380 }], [{ t: 'cats_booklet', x: 590, y: 380 }]],
  });
}
{ // 5 – Gap in the row – where does the chain stop? Find the gap in the row
  const s0 = sh(130, 230, 220, 14), s1 = sh(520, 420, 700);
  L.push({
    id: 'gap-in-the-row', par: 1, tools: { cats_book: 1 }, learn: ['chain_reaction'],
    statics: [s0, s1, { x: 1075, y: 640, w: 30, h: 220, skin: 'cats_post' }],
    fixed: [bookAt(s1, 300), bookAt(s1, 370), bookAt(s1, 440), bookAt(s1, 640), bookAt(s1, 710)],
    balls: [on(s0, -70, 13, { helper: true }), on(s1, 300, P)],
    goal: { type: 'bowl', x: 960, y: 720, w: 180 },
    objs: [{ t: 'duck', x: 540, y: 250 }],
    solution: [{ t: 'cats_book', x: 540, y: 340 }],
    fails: [[{ t: 'cats_book', x: 770, y: 340 }], [{ t: 'cats_book', x: 230, y: 340 }]],
  });
}
{ // 6 – The mouse goes first – no ball rolls; the mouse gives the first push (the plank is a decoy)
  const s1 = sh(560, 460, 620);
  L.push({
    id: 'mouse-starts', par: 1, tools: { cats_pendulum: 1, plank: 1 }, learn: ['chain_reaction', 'energy'],
    statics: [s1],
    fixed: [bookAt(s1, 340), bookAt(s1, 410), bookAt(s1, 480), bookAt(s1, 550)],
    balls: [on(s1, 250, P)],
    goal: { type: 'bowl', x: 980, y: 730, w: 180 },
    objs: [catAt(s1, 690, 1, { col: 'white', power: 5 }), { t: 'duck', x: 230, y: 330 }],
    solution: [{ t: 'cats_pendulum', x: 330, y: 300, a: 0 }],
    fails: [[{ t: 'plank', x: 300, y: 380, a: 15 }]],
  });
}

{ // 7 – The cat jumps – the yarn ball lies on the sleeping cat over a hole; wake it and it jumps away
  const s0 = sh(130, 230, 220, 14), s1 = sh(420, 420, 520), s2 = sh(830, 420, 100), s3 = sh(1030, 230, 180);
  L.push({
    id: 'cat-jumps', par: 2, tools: { cats_book: 2, plank: 1 }, learn: ['chain_reaction', 'energy'],
    statics: [s0, s1, s2, s3],
    fixed: [bookAt(s1, 300), bookAt(s1, 370), bookAt(s1, 540)],
    balls: [on(s0, -70, 13, { helper: true }), { x: 718, y: 348, r: P }],
    goal: { type: 'bowl', x: 730, y: 740, w: 170 },
    objs: [catAt(s1, 730, 1, { col: 'gray', mode: 'jump', to: { x: 1020, y: top(s3) - 23 } }), { t: 'duck', x: 455, y: 250 }],
    solution: [{ t: 'cats_book', x: 455, y: 330 }, { t: 'cats_book', x: 615, y: 330 }],
    fails: [[{ t: 'cats_book', x: 455, y: 330 }], [{ t: 'cats_book', x: 615, y: 330 }]],
  });
}
{ // 8 – The right way – direction: the mouse must swing the other way (flip it)
  const s1 = sh(530, 480, 220);
  L.push({
    id: 'right-way', par: 1, tools: { cats_pendulum: 1, cats_book: 1 }, learn: ['direction', 'energy'],
    statics: [s1, { x: 150, y: 590, w: 30, h: 380, skin: 'cats_post' }],
    balls: [on(s1, 70, P)],
    goal: { type: 'bowl', x: 290, y: 740, w: 190 },
    objs: [{ t: 'duck', x: 900, y: 420 }],
    solution: [{ t: 'cats_pendulum', x: 650, y: 300, a: 180 }],
    fails: [[{ t: 'cats_pendulum', x: 580, y: 320, a: 0 }], [{ t: 'cats_pendulum', x: 560, y: 320, a: 0 }]],
  });
}
{ // 9 – The stairs – books topple down the steps; two books close the gaps
  const s0 = sh(110, 200, 200, 14), t1 = sh(260, 300, 200), t2 = sh(435, 370, 150), t3 = sh(585, 440, 150), t4 = sh(780, 510, 240);
  L.push({
    id: 'stairs', par: 2, tools: { cats_book: 2, cats_booklet: 1 }, learn: ['chain_reaction', 'gravity'],
    statics: [s0, t1, t2, t3, t4, { x: 1095, y: 600, w: 30, h: 340, skin: 'cats_post' }],
    fixed: [bookAt(t1, 330)],
    balls: [on(s0, -60, 13, { helper: true }), on(t4, 40, P)],
    goal: { type: 'bowl', x: 990, y: 750, w: 170 },
    objs: [catAt(t4, 735, 1, { col: 'black', power: 4 }), { t: 'duck', x: 560, y: 240 }],
    solution: [{ t: 'cats_book', x: 480, y: 300 }, { t: 'cats_book', x: 590, y: 360 }],
    fails: [[{ t: 'cats_book', x: 480, y: 300 }], [{ t: 'cats_book', x: 590, y: 360 }], [{ t: 'cats_book', x: 480, y: 300 }, { t: 'cats_booklet', x: 590, y: 380 }]],
  });
}
{ // 10 – Mouse and book – the mouse starts the chain, but there is a gap in the row (big book, not the booklet)
  const s1 = sh(540, 460, 640);
  L.push({
    id: 'mouse-and-book', par: 2, tools: { cats_pendulum: 1, cats_booklet: 1, cats_book: 1 }, learn: ['chain_reaction', 'energy'],
    statics: [s1, { x: 1085, y: 650, w: 30, h: 220, skin: 'cats_post' }],
    fixed: [bookAt(s1, 330), bookAt(s1, 400), bookAt(s1, 580)],
    balls: [on(s1, 260, P)],
    goal: { type: 'bowl', x: 980, y: 740, w: 180 },
    objs: [catAt(s1, 710, 1, { col: 'orange', power: 4 }), { t: 'duck', x: 490, y: 260 }],
    solution: [{ t: 'cats_pendulum', x: 320, y: 300, a: 0 }, { t: 'cats_book', x: 490, y: 380 }],
    fails: [[{ t: 'cats_pendulum', x: 320, y: 300, a: 0 }], [{ t: 'cats_pendulum', x: 320, y: 300, a: 0 }, { t: 'cats_booklet', x: 490, y: 400 }], [{ t: 'cats_book', x: 490, y: 380 }]],
  });
}
{ // 11 – Far away – books press the button, the rope is cut, the tin falls on the seesaw
  const s0 = sh(1070, 200, 200, -14), s1 = sh(800, 300, 420);
  L.push({
    id: 'far-away', par: 2, tools: { cats_book: 2, cats_booklet: 1 }, learn: ['chain_reaction', 'mechanism', 'lever'], 
    statics: [s0, s1, { x: 522, y: 585, w: 10, h: 40, skin: 'cats_post' }, { x: 400, y: 700, w: 60, h: 200, skin: 'cats_post' }],
    fixed: [{ t: 'seesaw', x: 400, y: 560, a: 20 }, bookAt(s1, 930), bookAt(s1, 870)],
    balls: [{ x: 495, y: 570, r: P }, on(s0, 50, 13, { helper: true })],
    goal: { type: 'bell', x: 510, y: 380 },
    objs: [{ t: 'button', x: 620, y: 274, act: { cut: 'r1' } }, { t: 'rope', id: 'r1', ax: 310, ay: 100, hang: { t: 'stone', x: 310, y: 230 } }, { t: 'duck', x: 515, y: 300 }],
    solution: [{ t: 'cats_book', x: 770, y: 230 }, { t: 'cats_book', x: 680, y: 230 }],
    fails: [[{ t: 'cats_book', x: 770, y: 230 }], [{ t: 'cats_book', x: 680, y: 230 }]],
  });
}

{ // 12 – Two cats – a scratching board (plank) and a book wake the first cat, which swats a yarn ball to the second
  const s0 = sh(110, 160, 200, 14), s1 = sh(560, 330, 400), s2 = sh(820, 560, 160), s2b = sh(1095, 560, 100), s3 = sh(1060, 190, 160);
  L.push({
    id: 'two-cats', par: 2, tools: { plank: 1, cats_book: 1, cats_booklet: 1 }, learn: ['chain_reaction', 'energy', 'ramp'],
    statics: [s0, s1, s2, s2b, s3],
    fixed: [bookAt(s1, 430)],
    balls: [on(s0, -60, 13, { helper: true }), on(s1, 175, 13, { helper: true }), { x: 1000, y: 488, r: P }],
    goal: { type: 'bowl', x: 975, y: 770, w: 150 },
    objs: [catAt(s1, 650, 1, { col: 'orange', power: 4 }), catAt(s2, 980, -1, { col: 'gray', mode: 'jump', to: { x: 1060, y: top(s3) - 23 } }), { t: 'duck', x: 300, y: 420 }],
    solution: [{ t: 'plank', x: 280, y: 270, a: 15 }, { t: 'cats_book', x: 510, y: 250 }],
    fails: [[{ t: 'plank', x: 280, y: 270, a: 15 }], [{ t: 'cats_book', x: 510, y: 250 }]],
  });
}
{ // 13 – Off the shelf – the mouse starts it; the last book falls off the shelf onto the cat
  const s1 = sh(500, 300, 400), s2 = sh(760, 520, 260);
  L.push({
    id: 'off-the-shelf', par: 2, tools: { cats_pendulum: 1, cats_booklet: 1, cats_book: 1 }, learn: ['chain_reaction', 'gravity', 'energy'],
    statics: [s1, s2, { x: 1095, y: 600, w: 30, h: 340, skin: 'cats_post' }],
    fixed: [bookAt(s1, 400), bookAt(s1, 470), bookAt(s1, 540)],
    balls: [on(s2, 50, P)],
    goal: { type: 'bowl', x: 985, y: 760, w: 170 },
    objs: [catAt(s2, 720, 1, { col: 'white', power: 4 }), { t: 'duck', x: 610, y: 430 }],
    solution: [{ t: 'cats_pendulum', x: 390, y: 140, a: 0 }, { t: 'cats_book', x: 650, y: 200 }],
    fails: [[{ t: 'cats_pendulum', x: 390, y: 140, a: 0 }], [{ t: 'cats_book', x: 650, y: 200 }]],
  });
}
{ // 14 – Hop and topple – the cushion (pad) bounces the yarn ball up to the books
  const s0 = sh(140, 200, 220, 10), s1 = Object.assign(sh(730, 420, 440, 0, 'cats_sofa'), { drag: 0.9 });
  L.push({
    id: 'hop-and-topple', par: 2, tools: { pad: 1, cats_book: 1, plank: 1, cats_booklet: 1 }, learn: ['chain_reaction', 'spring', 'energy'],
    statics: [s0, s1, sh(705, 290, 290, 0, 'cats_sofa'), { x: 838, y: 258, w: 20, h: 46, skin: 'cats_post' }, { x: 1120, y: 600, w: 30, h: 340, skin: 'cats_post' }],
    fixed: [bookAt(s1, 600)],
    balls: [on(s0, -70, 13, { helper: true }), on(s1, 195, P)],
    goal: { type: 'bowl', x: 1030, y: 760, w: 170 },
    objs: [catAt(s1, 840, 1, { col: 'black', power: 4 }), { t: 'duck', x: 400, y: 330 }],
    solution: [{ t: 'pad', x: 360, y: 480, a: 15 }, { t: 'cats_book', x: 690, y: 330 }],
    fails: [[{ t: 'pad', x: 360, y: 480, a: 15 }], [{ t: 'cats_book', x: 690, y: 330 }]],
  });
}

{ // 15 – The master chain – from the corner to the basket: bridge, books and a jumping cat (several ways work)
  const s0 = sh(110, 160, 200, 14), s1 = sh(560, 330, 400), s2 = sh(700, 560, 180), s2b = sh(985, 560, 130), s3 = sh(1070, 220, 150);
  L.push({
    id: 'master-chain', par: 3, tools: { plank: 2, pad: 1, cats_pendulum: 1, cats_book: 3, cats_booklet: 2 }, learn: ['chain_reaction', 'energy', 'ramp', 'gravity'],
    statics: [s0, s1, s2, s2b, s3],
    fixed: [bookAt(s1, 430), bookAt(s1, 640)],
    balls: [on(s0, -60, 13, { helper: true }), { x: 850, y: 488, r: P }],
    goal: { type: 'bowl', x: 855, y: 770, w: 140 },
    objs: [catAt(s2, 830, -1, { col: 'orange', mode: 'jump', to: { x: 1070, y: top(s3) - 23 } }), { t: 'duck', x: 560, y: 450 }],
    solution: [{ t: 'plank', x: 280, y: 270, a: 15 }, { t: 'cats_book', x: 535, y: 250 }, { t: 'cats_book', x: 735, y: 250 }],
    fails: [[{ t: 'plank', x: 280, y: 270, a: 15 }, { t: 'cats_book', x: 535, y: 250 }], [{ t: 'plank', x: 280, y: 270, a: 15 }, { t: 'cats_book', x: 735, y: 250 }], [{ t: 'cats_book', x: 535, y: 250 }, { t: 'cats_book', x: 735, y: 250 }]],
  });
}

export const levels = defineLevels(L);
